import { formsApi } from "../../../lib/api";
import { useState, FormEvent } from "react";

export default function ContactHero() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      await formsApi.contact({
        first_name: (form.elements.namedItem("first_name") as HTMLInputElement)?.value ?? "",
        last_name:  (form.elements.namedItem("last_name")  as HTMLInputElement)?.value ?? "",
        email:      (form.elements.namedItem("email")      as HTMLInputElement)?.value ?? "",
        phone:      (form.elements.namedItem("phone")      as HTMLInputElement)?.value ?? "",
        message:    (form.elements.namedItem("message")    as HTMLTextAreaElement)?.value ?? "",
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-32 pb-20 bg-cl-tint">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left — intro */}
          <div className="pt-4">
            <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-5 thick-border cartoon-shadow-sm">
              <i className="ri-customer-service-2-line text-cl-dark text-xs"></i>
              <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Need some help</span>
            </div>

            <h1 className="font-black text-cl-dark leading-[1.05] mb-5" style={{ fontSize: "clamp(2.6rem, 5vw, 4rem)" }}>
              Contact us
            </h1>

            <p className="text-cl-dark/65 text-base leading-relaxed font-semibold mb-4 max-w-md">
              Whether you need help with a quote, need to pay for a service or just have a general question, we have customer service which is available 7 days a week.
            </p>
            <p className="text-cl-dark/65 text-base leading-relaxed font-semibold mb-4 max-w-md">
              As well as WhatsApp, we provide a callback service at a time to suit you.
            </p>
            <p className="text-cl-dark/65 text-base leading-relaxed font-semibold mb-8 max-w-md">
              Alternatively, we can call you back almost immediately, if you want to speak right away without having to waste time in a call queue.
            </p>

            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-cl-green text-white font-black px-7 py-3.5 rounded-full text-sm cartoon-btn-green whitespace-nowrap mb-8"
            >
              <i className="ri-calendar-check-line"></i>
              Book now
            </Link>

            {/* Useful links */}
            <div className="bg-white rounded-2xl px-5 py-4 thick-border cartoon-shadow-sm inline-block">
              <p className="text-xs font-black text-cl-dark uppercase tracking-widest mb-3">Useful links</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: "Order tracking", icon: "ri-map-pin-time-line" },
                  { label: "Update booking", icon: "ri-edit-line" },
                  { label: "Add notes to booking", icon: "ri-sticky-note-line" },
                ].map((link) => (
                  <a
                    key={link.label}
                    href="#"
                    className="flex items-center gap-1.5 text-cl-green font-black text-xs hover:text-cl-dark transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${link.icon} text-xs`}></i>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-cl-dark rounded-3xl p-8 thick-border cartoon-shadow">
            <h2 className="text-cl-lime font-black text-xl mb-6 flex items-center gap-2">
              <i className="ri-mail-send-line"></i>
              Message us
            </h2>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-cl-lime mb-4 thick-border">
                  <i className="ri-check-line text-cl-dark text-2xl font-black"></i>
                </div>
                <p className="text-white font-black text-lg mb-2">Message sent!</p>
                <p className="text-white/60 text-sm font-semibold">We&apos;ll get back to you within 1 business day.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-us-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="First Name"
                      className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cl-lime transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Last Name"
                      className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cl-lime transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Email Address"
                    className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cl-lime transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">Phone Number</label>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2 bg-white/10 border-2 border-white/20 rounded-xl px-3 py-3 flex-shrink-0">
                      <span className="text-sm">🇬🇧</span>
                      <span className="text-white/70 text-sm font-semibold">+44</span>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      className="flex-1 bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cl-lime transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">Service *</label>
                  <select
                    name="service"
                    required
                    className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white/70 focus:outline-none focus:border-cl-lime transition-colors cursor-pointer appearance-none"
                    style={{ background: "rgba(255,255,255,0.08)" }}
                  >
                    <option value="" className="text-cl-dark bg-white">Select a service</option>
                    <option value="Man & Van Rubbish Removal Service" className="text-cl-dark bg-white">Man &amp; Van</option>
                    <option value="Sofa Removal" className="text-cl-dark bg-white">Sofa Removal</option>
                    <option value="Bin Collection" className="text-cl-dark bg-white">Private Bin Collection</option>
                    <option value="Furniture Removal" className="text-cl-dark bg-white">Furniture Removal</option>
                    <option value="House Clearance" className="text-cl-dark bg-white">House Clearance</option>
                    <option value="Appliance Removal" className="text-cl-dark bg-white">Appliance Removal</option>
                    <option value="General Enquiry" className="text-cl-dark bg-white">General Enquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">Query *</label>
                  <select
                    name="query"
                    required
                    className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white/70 focus:outline-none focus:border-cl-lime transition-colors cursor-pointer appearance-none"
                    style={{ background: "rgba(255,255,255,0.08)" }}
                  >
                    <option value="" className="text-cl-dark bg-white">Select a query type</option>
                    <option value="Get a quote" className="text-cl-dark bg-white">Get a quote</option>
                    <option value="Existing booking" className="text-cl-dark bg-white">Existing booking</option>
                    <option value="Complaint" className="text-cl-dark bg-white">Complaint</option>
                    <option value="Payment query" className="text-cl-dark bg-white">Payment query</option>
                    <option value="Other" className="text-cl-dark bg-white">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/70 text-xs font-black mb-1.5 uppercase tracking-wide">
                    Your Message * <span className={`font-semibold ml-1 ${charCount > 450 ? "text-red-400" : "text-white/40"}`}>({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Your Message"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cl-lime transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-cl-lime text-cl-dark font-black py-4 rounded-2xl text-sm cartoon-btn transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-fill"></i> Send message
                    </span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
